import { apiClient } from '../client';

export interface AnalyticsQuery {
  school_id?: string;
  period?: 'week' | 'month' | 'all';
}

// Session Analytics
export interface SessionAnalytics {
  averageSessionDuration: number;
  dailyActiveUsers: number;
  weeklyActiveUsers: number;
  monthlyActiveUsers: number;
  totalSessions: number;
  sessionsPerUser: number;
  peakHours: Array<{ hour: number; sessions: number }>;
}

// Messaging Analytics
export interface MessagingAnalytics {
  totalMessagesSent: number;
  activeConversations: number;
  activeMessagingUsers: number;
  replyRate: number;
  averageResponseTimeMinutes: number;
  mostActiveConversations: Array<{
    chatId: string;
    messageCount: number;
    participants: string[];
  }>;
}

// Profile Views Analytics
export interface ProfileViewsAnalytics {
  totalProfileViews: number;
  uniqueViewers: number;
  uniqueProfilesViewed: number;
  viewToConnectionRate: number;
  mostViewedProfiles: Array<{
    userId: string;
    userName: string;
    viewCount: number;
    connectionsMade: number;
  }>;
}

// Engagement Analytics
export interface EngagementAnalytics {
  events: {
    totalViewed: number;
    totalAttendances: number;
    conversionRate: number;
  };
  ideas: {
    totalViewed: number;
    totalInterested: number;
    totalCollaborations: number;
    interestRate: number;
    collaborationRate: number;
  };
  spaces: {
    totalViewed: number;
    totalJoins: number;
    conversionRate: number;
  };
}

// User Lifecycle Analytics
export interface UserLifecycleAnalytics {
  newUsers: {
    total: number;
    withConnections: number;
    firstWeekConnectionRate: number;
  };
  ghostUsers: {
    total: number;
    percentage: number;
  };
  churnedUsers: {
    total: number;
    churnRate: number;
  };
  retentionCohorts: Array<{
    week: string;
    newUsers: number;
    retained: number;
    retentionRate: number;
  }>;
  registrationsByDate?: Array<{
    date: string;
    registrations: number;
  }>;
  activityByDate?: Array<{
    date: string;
    activeUsers: number;
  }>;
}

// Social Graph Analytics
export interface SocialGraphAnalytics {
  totalConnections: number;
  networkGrowthRate: number;
  averageConnectionsPerUser: number;
  connectionDistribution: {
    zeroConnections: number;
    oneToFive: number;
    sixToTen: number;
    elevenToTwenty: number;
    twentyOnePlus: number;
  };
  mostConnectedUsers: Array<{
    userId: string;
    userName: string;
    connectionCount: number;
  }>;
}

// Referrals Analytics
export interface ReferralsAnalytics {
  totalReferred: number;
  totalAccepted: number;
  acceptanceRate: number;
  viralCoefficient: number;
  topReferrers: Array<{
    userId: string;
    userName: string;
    referralCount: number;
    acceptedCount: number;
    weeklyGrowth: number;
  }>;
}

// Safety Analytics
export interface SafetyAnalytics {
  privacyAdoption: {
    locationObfuscationEnabled: number;
    locationObfuscationPercentage: number;
  };
  reports: {
    totalReports: number;
    byType: Record<string, number>;
    resolved: number;
    pending: number;
  };
  incidentTimeline: Array<{
    date: string;
    reportCount: number;
  }>;
  mostReportedContent: Array<{
    contentType: string;
    contentId: string;
    reportCount: number;
  }>;
}

// Health Check
export interface HealthCheck {
  status: 'healthy' | 'degraded' | 'error';
  timestamp: string;
  checks: {
    database?: {
      status: string;
      message?: string;
    };
    activityLogging?: {
      status: string;
      totalActivities?: number;
    };
    sessionTracking?: {
      status: string;
      totalSessions?: number;
    };
    profileViews?: {
      status: string;
      totalViews?: number;
    };
  };
  system?: {
    uptime: number;
    memory: {
      used: number;
      total: number;
      unit: string;
    };
    nodeVersion: string;
  };
}

export const analyticsAPI = {
  // Session Analytics
  getSessionAnalytics: async (params: AnalyticsQuery = {}): Promise<SessionAnalytics> => {
    const response = await apiClient.get('/api/admin/analytics/session-analytics', { params });
    return response.data;
  },

  // Messaging Analytics
  getMessagingAnalytics: async (params: AnalyticsQuery = {}): Promise<MessagingAnalytics> => {
    const response = await apiClient.get('/api/admin/analytics/messaging', { params });
    return response.data;
  },

  // Profile Views Analytics
  getProfileViewsAnalytics: async (params: AnalyticsQuery = {}): Promise<ProfileViewsAnalytics> => {
    const response = await apiClient.get('/api/admin/analytics/profile-views', { params });
    return response.data;
  },

  // Engagement Analytics
  getEngagementAnalytics: async (params: AnalyticsQuery = {}): Promise<EngagementAnalytics> => {
    const response = await apiClient.get('/api/admin/analytics/engagement', { params });
    return response.data;
  },

  // User Lifecycle Analytics
  getUserLifecycleAnalytics: async (params: AnalyticsQuery = {}): Promise<UserLifecycleAnalytics> => {
    const response = await apiClient.get('/api/admin/analytics/user-lifecycle', { params });
    return response.data;
  },

  // Social Graph Analytics
  getSocialGraphAnalytics: async (params: AnalyticsQuery = {}): Promise<SocialGraphAnalytics> => {
    const response = await apiClient.get('/api/admin/analytics/social-graph', { params });
    return response.data;
  },

  // Referrals Analytics
  getReferralsAnalytics: async (params: AnalyticsQuery = {}): Promise<ReferralsAnalytics> => {
    const response = await apiClient.get('/api/admin/analytics/referrals', { params });
    return response.data;
  },

  // Safety Analytics
  getSafetyAnalytics: async (params: AnalyticsQuery = {}): Promise<SafetyAnalytics> => {
    const response = await apiClient.get('/api/admin/analytics/safety', { params });
    return response.data;
  },

  // Health Check
  getHealthCheck: async (): Promise<HealthCheck> => {
    const response = await apiClient.get('/api/admin/analytics/health');
    return response.data;
  },
};
